// SEQ-180 phase 2, task P21 (plan decision D21): a bucket click opens a
// narrower call in a panel. It never cross-filters the dashboard; that is
// phase 3 and stays there.
//
// GRAIN LADDER. company to project, project to month, any period grain to
// project inside that period, and every "attribute" grain (builder, status,
// project manager, site manager) to the projects carrying that attribute.
//
// The RPC's request shape only filters by project ids, statuses and dates, so
// an attribute bucket is resolved to a LIST OF PROJECT IDS on the client from
// the projects the page already loaded, then passed as `projectIds`. That is
// exact, not approximate: the same `claims_projects` columns the RPC groups
// by are the ones read here.

import type { DateRange } from "../../lib/pnl/fiscalYear";
import type { PnlGroupBy } from "../../lib/pnl/analyticsClient";
import { periodBucketRange } from "./periodBucket";

export interface DrillDownProject {
  id: string;
  job_location: string;
  status?: string | null;
  builder_name?: string | null;
  project_manager?: string | null;
  site_manager?: string | null;
}

export interface DrillDownSource {
  /** The grain the clicked widget was showing. */
  groupBy: string;
  /** The clicked bucket. */
  bucketLabel: string;
  projectId: string | null;
  periodMonth: string | null;
  /**
   * The clicked bucket's key. A quarter, calendar year or financial year row
   * carries no `periodMonth`, so its period is read from the key (SEQ-240).
   */
  bucketKey?: string;
  /** The range the widget itself was showing, which the drill stays inside. */
  range: DateRange;
  projectIds: string[];
  statuses: string[];
}

export interface DrillDownRequest {
  groupBy: PnlGroupBy;
  range: DateRange;
  projectIds: string[];
  statuses: string[];
  /** What the panel says it is showing, e.g. "Projects in Aug 2026". */
  heading: string;
  /** Null when the drill can be made; a plain sentence when it cannot. */
  unavailableReason: string | null;
}

/** The last day of a month, for narrowing a month bucket to its own range. */
function monthRange(periodMonth: string): DateRange {
  const [year, month] = periodMonth.split("-").map(Number);
  const lastDay = new Date(year, month, 0).getDate();
  return { from: `${periodMonth.slice(0, 7)}-01`, to: `${periodMonth.slice(0, 7)}-${String(lastDay).padStart(2, "0")}` };
}

/** The overlap of two inclusive ranges; a null bound is open. */
function intersectRanges(a: DateRange, b: DateRange): DateRange {
  const from = a.from === null ? b.from : b.from === null ? a.from : a.from > b.from ? a.from : b.from;
  const to = a.to === null ? b.to : b.to === null ? a.to : a.to < b.to ? a.to : b.to;
  return { from, to };
}

const ATTRIBUTE_FIELD: Record<string, keyof DrillDownProject> = {
  builder: "builder_name",
  project_manager: "project_manager",
  site_manager: "site_manager",
};

/**
 * Projects whose attribute matches the bucket's label. A bucket the RPC
 * labelled as the no-value bucket matches the projects with a null or blank
 * value, so "(no project manager)" drills to exactly the projects that are
 * missing one.
 */
export function projectIdsForAttributeBucket(
  projects: DrillDownProject[],
  field: keyof DrillDownProject,
  bucketLabel: string,
): string[] {
  const label = bucketLabel.trim().toLowerCase();
  const isNoValueBucket = label.startsWith("(no ") || label === "unassigned" || label === "";

  return projects
    .filter((project) => {
      const raw = project[field];
      const value = typeof raw === "string" ? raw.trim() : "";
      return isNoValueBucket ? value === "" : value.toLowerCase() === label;
    })
    .map((project) => project.id);
}

export function buildDrillDownRequest(source: DrillDownSource, projects: DrillDownProject[]): DrillDownRequest {
  const base = {
    range: source.range,
    projectIds: source.projectIds,
    statuses: source.statuses,
    unavailableReason: null as string | null,
  };

  // A project bucket drills into that project's own months.
  if (source.groupBy === "project" || source.projectId !== null) {
    if (!source.projectId) {
      return { ...base, groupBy: "month", heading: `Months in ${source.bucketLabel}`, unavailableReason: "This bucket carries no project to drill into." };
    }
    return { ...base, groupBy: "month", projectIds: [source.projectId], heading: `${source.bucketLabel} by month` };
  }

  // A month bucket drills into the projects that reported that month.
  if (source.periodMonth !== null) {
    return { ...base, groupBy: "project", range: monthRange(source.periodMonth), heading: `Projects in ${source.bucketLabel}` };
  }

  // A quarter, calendar year or financial year bucket opens exactly that
  // period, cut to the widget's own range so the list adds up to the bar.
  // SEQ-240 audit B, row 23: this used to keep the whole range, so the 2026-Q3
  // bar ($2.4M) opened a list that added to the whole financial year.
  if (source.groupBy === "quarter" || source.groupBy === "calendar_year" || source.groupBy === "financial_year") {
    const period = source.bucketKey ? periodBucketRange(source.groupBy, source.bucketKey) : null;
    if (!period) {
      return {
        ...base,
        groupBy: "project",
        heading: `Projects in ${source.bucketLabel}`,
        unavailableReason: "This bucket's period could not be read, so its projects cannot be listed.",
      };
    }
    return { ...base, groupBy: "project", range: intersectRanges(source.range, period), heading: `Projects in ${source.bucketLabel}` };
  }

  if (source.groupBy === "company") {
    return { ...base, groupBy: "project", heading: "Projects in this total" };
  }

  if (source.groupBy === "status") {
    return {
      ...base,
      groupBy: "project",
      statuses: [source.bucketLabel.toLowerCase()],
      heading: `${source.bucketLabel} projects`,
    };
  }

  const field = ATTRIBUTE_FIELD[source.groupBy];
  if (field) {
    const matched = projectIdsForAttributeBucket(projects, field, source.bucketLabel);
    if (matched.length === 0) {
      return {
        ...base,
        groupBy: "project",
        heading: `Projects for ${source.bucketLabel}`,
        unavailableReason: "No project on this page carries that value, so there is nothing to list.",
      };
    }
    // Intersect with the page's own project filter so a drill can never widen
    // what the user is allowed to be looking at.
    const projectIds = source.projectIds.length === 0 ? matched : matched.filter((id) => source.projectIds.includes(id));
    return { ...base, groupBy: "project", projectIds, heading: `Projects for ${source.bucketLabel}` };
  }

  return {
    ...base,
    groupBy: "project",
    heading: source.bucketLabel,
    unavailableReason: `Drilling into a ${source.groupBy} bucket is not supported yet.`,
  };
}
